import { z } from 'zod';
import { baseRow, cents, dateOnly, email, fileRef, id, listInput, nonEmpty, nonNegativeCents, optionalText, updateOf } from './common';

// ---------------------------------------------------------------------------
// Portal grants: which projects a portal-enabled contact can see, and what they can do.
// ---------------------------------------------------------------------------

export const PORTAL_CAPABILITIES = [
  'view_progress',
  'view_schedule',
  'view_documents',
  'view_photos',
  'make_selections',
  'approve_change_orders',
  'view_invoices',
  'pay_invoices',
  'message',
] as const;
export const portalCapability = z.enum(PORTAL_CAPABILITIES);
export type PortalCapability = z.infer<typeof portalCapability>;

export const DEFAULT_PORTAL_CAPABILITIES: PortalCapability[] = [...PORTAL_CAPABILITIES];

export const createPortalGrantInput = z.object({
  contactId: id,
  projectId: id,
  capabilities: z.array(portalCapability).default(DEFAULT_PORTAL_CAPABILITIES),
  sendInvite: z.boolean().default(true),
  message: optionalText(2000),
});
export type CreatePortalGrantInput = z.infer<typeof createPortalGrantInput>;
export const updatePortalGrantInput = z.object({
  id,
  capabilities: z.array(portalCapability).optional(),
  revoked: z.boolean().optional(),
});
export const portalGrantSchema = baseRow.extend({
  contactId: id,
  projectId: id,
  capabilities: z.array(portalCapability),
  invitedAt: z.date().nullable(),
  lastAccessAt: z.date().nullable(),
  revokedAt: z.date().nullable(),
});
export type PortalGrant = z.infer<typeof portalGrantSchema>;
export const listPortalGrantsInput = listInput.extend({ projectId: id.optional(), contactId: id.optional() });

export const portalMagicLinkInput = z.object({
  email,
  workspaceSlug: z.string().min(1).max(64),
  callbackURL: z.string().max(2048).optional(),
});
export type PortalMagicLinkInput = z.infer<typeof portalMagicLinkInput>;

// ---------------------------------------------------------------------------
// Messages (client <-> PM thread per project)
// ---------------------------------------------------------------------------

export const createMessageInput = z.object({
  projectId: id,
  body: z.string().trim().min(1).max(20_000),
  attachments: z.array(fileRef).max(20).default([]),
  /** Internal note visible to team only (never sent to portal). */
  internal: z.boolean().default(false),
});
export type CreateMessageInput = z.infer<typeof createMessageInput>;
export const messageSchema = baseRow.extend({
  projectId: id,
  authorUserId: id.nullable(),
  authorContactId: id.nullable(),
  body: z.string(),
  attachments: z.array(fileRef),
  internal: z.boolean(),
  readByClientAt: z.date().nullable(),
  readByTeamAt: z.date().nullable(),
});
export type Message = z.infer<typeof messageSchema>;
export const listMessagesInput = listInput.extend({ projectId: id });

// ---------------------------------------------------------------------------
// Selections (finishes, fixtures, options the client chooses)
// ---------------------------------------------------------------------------

export const SELECTION_STATUSES = ['draft', 'pending_client', 'selected', 'approved', 'ordered', 'installed'] as const;
export const selectionStatus = z.enum(SELECTION_STATUSES);
export type SelectionStatus = z.infer<typeof selectionStatus>;

export const selectionOption = z.object({
  id: z.string().min(1).max(64),
  name: nonEmpty(200),
  description: z.string().max(5000).optional(),
  priceCents: cents.default(0),
  imageKeys: z.array(z.string().max(1024)).max(20).default([]),
  vendor: z.string().max(200).optional(),
  sku: z.string().max(120).optional(),
  leadTimeDays: z.number().int().min(0).max(3650).optional(),
});
export type SelectionOption = z.infer<typeof selectionOption>;

export const createSelectionInput = z.object({
  projectId: id,
  category: nonEmpty(120),
  name: nonEmpty(200),
  description: optionalText(10_000),
  location: z.string().trim().max(200).optional().nullable(),
  allowanceCents: nonNegativeCents.default(0),
  options: z.array(selectionOption).max(50).default([]),
  dueDate: dateOnly.optional().nullable(),
  status: selectionStatus.default('draft'),
});
export type CreateSelectionInput = z.infer<typeof createSelectionInput>;
export const updateSelectionInput = updateOf(createSelectionInput.omit({ projectId: true }));
export const selectionSchema = baseRow.extend({
  projectId: id,
  category: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  allowanceCents: cents,
  options: z.array(selectionOption),
  chosenOptionId: z.string().nullable(),
  chosenByContactId: id.nullable(),
  chosenAt: z.date().nullable(),
  dueDate: z.string().nullable(),
  status: selectionStatus,
  changeOrderId: id.nullable(),
});
export type Selection = z.infer<typeof selectionSchema>;
export const chooseSelectionInput = z.object({ id, optionId: z.string().min(1).max(64), note: optionalText(2000) });
export const listSelectionsInput = listInput.extend({ projectId: id, status: selectionStatus.optional() });

// ---------------------------------------------------------------------------
// Portal views (read models returned to the client)
// ---------------------------------------------------------------------------

export const portalProjectSummary = z.object({
  projectId: id,
  name: z.string(),
  status: z.string(),
  percentComplete: z.number().int(),
  startDate: z.string().nullable(),
  targetEndDate: z.string().nullable(),
  pmName: z.string().nullable(),
  pmEmail: z.string().nullable(),
  capabilities: z.array(portalCapability),
  openChangeOrders: z.number().int(),
  openInvoices: z.number().int(),
  balanceDueCents: cents,
  pendingSelections: z.number().int(),
  unreadMessages: z.number().int(),
});
export type PortalProjectSummary = z.infer<typeof portalProjectSummary>;

// ---------------------------------------------------------------------------
// Portal: GC-side access controls
// ---------------------------------------------------------------------------

export const setContactPortalEnabledInput = z.object({ contactId: id, enabled: z.boolean() });
export type SetContactPortalEnabledInput = z.infer<typeof setContactPortalEnabledInput>;

export const resendPortalInviteInput = z.object({ id, message: optionalText(2000) });

/** Records the team can flag as shared with the client from the portal settings. */
export const PORTAL_SHAREABLE_ENTITIES = ['photo', 'document', 'schedule_task', 'change_order'] as const;
export const portalShareableEntity = z.enum(PORTAL_SHAREABLE_ENTITIES);
export type PortalShareableEntity = z.infer<typeof portalShareableEntity>;

export const listPortalShareableInput = z.object({ projectId: id, entityType: portalShareableEntity });
export const setPortalSharingInput = z.object({
  projectId: id,
  entityType: portalShareableEntity,
  ids: z.array(id).min(1).max(200),
  shared: z.boolean(),
});
export type SetPortalSharingInput = z.infer<typeof setPortalSharingInput>;

export const portalPreviewInput = z.object({ projectId: id, contactId: id.optional() });
export type PortalPreviewInput = z.infer<typeof portalPreviewInput>;

export const listPortalInboxInput = z.object({
  projectId: id.optional(),
  unreadOnly: z.boolean().default(false),
  search: z.string().trim().max(200).optional(),
});

export const markPortalThreadReadInput = z.object({ projectId: id });

export const sendSelectionToClientInput = z.object({ id, notify: z.boolean().default(true) });
export const setSelectionStatusInput = z.object({ id, status: z.enum(['ordered', 'installed', 'pending_client', 'draft']) });

// ---------------------------------------------------------------------------
// Portal: sign-in and branding (public)
// ---------------------------------------------------------------------------

/** A path inside the portal (`/portal/...`) to land on after the magic link. */
export const portalRedirectPath = z
  .string()
  .max(512)
  .regex(/^\/portal(\/[A-Za-z0-9\-._~%/?=&]*)?$/, 'Invalid redirect');

export const requestPortalLinkInput = z.object({
  email,
  /** Optional: when omitted, every portal the address can use is considered. */
  workspaceSlug: z.string().trim().toLowerCase().min(1).max(64).optional(),
  redirectPath: portalRedirectPath.optional(),
});
export type RequestPortalLinkInput = z.infer<typeof requestPortalLinkInput>;

export const portalBrandingInput = z.object({ workspaceSlug: z.string().trim().toLowerCase().min(1).max(64) });

// ---------------------------------------------------------------------------
// Portal: client actions
// ---------------------------------------------------------------------------

export const portalProjectInput = z.object({ projectId: id });
export const portalPhotosInput = z.object({
  projectId: id,
  cursor: z.string().max(512).optional().nullable(),
  limit: z.number().int().min(1).max(100).default(24),
  /** Only photos in this (client-shared) album. */
  albumId: id.optional().nullable(),
});
export const portalByIdInput = z.object({ id });

export const portalSignatureInput = z.object({
  signerName: nonEmpty(200),
  /** PNG data URL of the drawn signature; omitted for a typed signature. */
  imageDataUrl: z
    .string()
    .max(500_000)
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Signature must be a PNG image')
    .optional(),
  typed: z.boolean().default(false),
  /** The client must tick "I agree" before signing. */
  agreed: z.literal(true, { message: 'You must agree before signing' }),
});
export type PortalSignatureInput = z.infer<typeof portalSignatureInput>;

export const portalApproveChangeOrderInput = z.object({ id, signature: portalSignatureInput });
export const portalDeclineChangeOrderInput = z.object({ id, reason: z.string().trim().min(1).max(2000) });

export const portalChooseSelectionInput = chooseSelectionInput;
export const portalApproveSelectionInput = z.object({ id, note: optionalText(2000) });

export const PORTAL_PAYMENT_METHODS = ['card', 'ach', 'any'] as const;
export const portalPaymentMethod = z.enum(PORTAL_PAYMENT_METHODS);
export type PortalPaymentMethod = z.infer<typeof portalPaymentMethod>;
export const portalPayInvoiceInput = z.object({
  id,
  method: portalPaymentMethod.default('any'),
  /** Partial payment; defaults to the full balance. */
  amountCents: z.number().int().positive().optional(),
});
export type PortalPayInvoiceInput = z.infer<typeof portalPayInvoiceInput>;

export const portalSendMessageInput = z.object({
  projectId: id,
  body: z.string().trim().min(1).max(20_000),
  attachments: z.array(fileRef).max(20).default([]),
});
export type PortalSendMessageInput = z.infer<typeof portalSendMessageInput>;

export const portalListMessagesInput = z.object({
  projectId: id,
  cursor: z.string().max(512).optional().nullable(),
  limit: z.number().int().min(1).max(200).default(100),
});

export const portalAttachmentUploadInput = z.object({
  projectId: id,
  fileName: nonEmpty(255),
  contentType: z.string().max(255).default('application/octet-stream'),
  size: z.number().int().positive().max(50 * 1024 * 1024),
});
export const portalAttachmentUrlInput = z.object({ messageId: id, key: z.string().min(1).max(1024) });
